import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Phone, Star, ShieldCheck, ClipboardCheck, Hammer, Camera, ShoppingBag, TrendingDown } from 'lucide-react';
import Layout from '@/components/Layout';
import ServiceCard from '@/components/ServiceCard';
import TrustRow from '@/components/TrustRow';
import CtaBand from '@/components/CtaBand';
import { Button } from '@/components/ui/button';
import { api, fileUrl, thumb } from '@/lib/api';

const HERO_IMG = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80';
const ACCENT_IMG = 'https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=1200&q=80';

export default function HomePage() {
  const [cats, setCats] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [picks, setPicks] = useState([]);
  const [site, setSite] = useState(null);

  useEffect(() => {
    api.get('/services').then(r => setCats(r.data.categories || []));
    api.get('/reviews', { params: { limit: 3 } }).then(r => setReviews(r.data.reviews || []));
    api.get('/gallery', { params: { limit: 6 } }).then(r => setGallery(r.data.items || []));
    api.get('/site').then(r => setSite(r.data.site || null)).catch(() => {});
    api.get('/shop/ai-recommendations', { params: { limit: 6 } })
      .then(r => setPicks(r.data.products || []))
      .catch(() => setPicks([]));
  }, []);

  // Admin-editable hero content (Website Editor) with safe defaults
  const hero = site?.hero || {};
  const heroImg = hero.image_url ? fileUrl(hero.image_url) : HERO_IMG;
  const heroEyebrow = hero.eyebrow || 'South Florida • Fully Insured';
  const heroHeadline = hero.headline_top || 'Reliable Home & Commercial Services';
  const heroAccent = hero.headline_accent || 'Across South Florida';
  const heroSubhead = hero.subhead || 'Handyman, Flooring, Painting, Cleaning, Landscaping, Remodeling and more. Trusted by homeowners and businesses from Palm Beach to Miami.';

  return (
    <Layout>
      {/* HERO */}
      <section className="relative bg-hero-tint border-b border-[#E6E9EE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#C8922F] font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C8922F]" /> {heroEyebrow}
            </div>
            <h1 data-testid="home-hero-headline" className="mt-3 font-headline text-4xl sm:text-5xl lg:text-6xl text-[#17352D] leading-[1.02] tracking-[-0.02em]">
              {heroHeadline} <span className="text-[#C8922F]">{heroAccent}</span>
            </h1>
            <p className="mt-5 text-slate-700 text-base md:text-lg max-w-xl">
              {heroSubhead}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild data-testid="home-hero-get-estimate-button" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12 px-6">
                <Link to="/estimate"><Sparkles className="h-4 w-4 mr-2" /> Get Free Estimate</Link>
              </Button>
              <Button asChild data-testid="home-hero-book-button" variant="outline" className="rounded-xl border-[#17352D] text-[#17352D] hover:bg-[#F2F7F5] h-12 px-6">
                <Link to="/booking"><ClipboardCheck className="h-4 w-4 mr-2" /> Book Service</Link>
              </Button>
              <Button asChild data-testid="home-hero-browse-button" variant="ghost" className="rounded-xl text-[#17352D] hover:bg-[#F2F7F5] h-12 px-6">
                <Link to="/services">Browse Services <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
            </div>
            <a href="tel:5618848900" data-testid="home-hero-call-link" className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700 hover:text-[#17352D]">
              <Phone className="h-4 w-4 text-[#C8922F]" /> Or call <span className="font-semibold">561-884-8900</span>
            </a>
            <TrustRow className="mt-7" />
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden border border-[#EED7A6] shadow-[0_30px_80px_rgba(11,18,32,0.18)]">
              <img src={heroImg} alt="Luxury South Florida home with palms and pool" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/60 via-black/20 to-transparent text-white">
                <div className="text-xs uppercase tracking-[0.18em] opacity-90">Project: Palm Beach Gardens</div>
                <div className="font-headline text-xl mt-1">Complete Pool Deck & Landscape Refresh</div>
              </div>
            </div>
            {/* floating badge */}
            <div className="hidden md:flex absolute -left-6 top-6 bg-white rounded-2xl border border-[#E6E9EE] shadow-xl p-3 items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#FBF7EE] border border-[#EED7A6] flex items-center justify-center"><Star className="h-5 w-5 text-[#C8922F]" /></div>
              <div>
                <div className="text-xs text-slate-500">Google Reviews</div>
                <div className="font-semibold text-[#17352D]">4.9 / 5 • 240+ jobs</div>
              </div>
            </div>
            <div className="hidden md:flex absolute -right-4 -bottom-6 bg-white rounded-2xl border border-[#E6E9EE] shadow-xl p-3 items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#F2F7F5] border border-[#C7DED4] flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-[#17352D]" /></div>
              <div>
                <div className="text-xs text-slate-500">Fully Insured</div>
                <div className="font-semibold text-[#17352D]">South Florida</div>
              </div>
            </div>
          </div>
        </div>

        {/* category chips */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
            {cats.slice(0, 12).map(c => (
              <Link key={c.slug} to={`/services/${c.slug}`}
                data-testid={`home-category-chip-${c.slug}`}
                className="shrink-0 rounded-full border border-[#E6E9EE] bg-white px-4 py-2 text-sm text-slate-700 hover:border-[#17352D] hover:text-[#17352D] transition-colors-only">
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Our Services</div>
            <h2 className="font-headline text-3xl md:text-4xl text-[#17352D] mt-1">One trusted service for everything</h2>
          </div>
          <Link to="/services" data-testid="home-view-all-services-link" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#17352D] hover:text-[#1E4338]">
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {cats.slice(0, 9).map(c => <ServiceCard key={c.slug} category={c} />)}
        </div>
        <div className="mt-6 md:hidden">
          <Link to="/services" className="text-sm font-semibold text-[#17352D]">View all services →</Link>
        </div>
      </section>

      {/* RES vs COMMERCIAL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { title: 'Residential', sub: 'Concierge service for homeowners across Palm Beach, Broward, and Miami-Dade.', img: HERO_IMG, to: '/residential', testid: 'home-residential-card' },
            { title: 'Commercial', sub: 'Recurring maintenance & project work for offices, HOAs, restaurants, and retail.', img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80', to: '/commercial', testid: 'home-commercial-card' },
          ].map(c => (
            <Link key={c.title} to={c.to} data-testid={c.testid} className="group relative rounded-3xl overflow-hidden border border-[#E6E9EE] shadow-[0_20px_50px_rgba(11,18,32,0.10)] card-hover">
              <img src={c.img} alt={c.title} className="h-72 md:h-96 w-full object-cover transition-transform-only group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 text-white">
                <div className="text-xs uppercase tracking-[0.2em] opacity-80">For {c.title === 'Residential' ? 'Homeowners' : 'Businesses'}</div>
                <div className="font-headline text-3xl md:text-4xl mt-1">{c.title} Services</div>
                <p className="text-sm opacity-90 mt-2 max-w-md">{c.sub}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold">Explore <ArrowRight className="h-4 w-4" /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* AI-RECOMMENDED PRODUCTS */}
      {picks.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="home-ai-picks-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold inline-flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" /> AI-Picked For You
              </div>
              <h2 className="font-headline text-3xl md:text-4xl text-[#17352D] mt-1">
                Hand-selected home essentials
              </h2>
              <p className="mt-2 text-sm text-slate-600 max-w-xl">
                Our AI ranks in-stock items by price competitiveness, profit health, and freshness — so what you see is what your neighbors are actually buying.
              </p>
            </div>
            <Link
              to="/shop"
              data-testid="home-view-shop-link"
              className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#17352D] hover:text-[#1E4338]"
            >
              Shop all products <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {picks.map((p) => (
              <Link
                key={p.id}
                to={`/shop/${p.id}`}
                data-testid={`home-ai-pick-${p.id}`}
                className="group relative rounded-2xl overflow-hidden border border-[#E6E9EE] bg-white shadow-[0_10px_30px_rgba(11,18,32,0.06)] hover:shadow-[0_18px_44px_rgba(11,18,32,0.12)] transition-shadow duration-200"
              >
                <div className="aspect-square bg-[#F2F7F5] overflow-hidden">
                  {p.image ? (
                    <img
                      src={thumb(p.image, 400)}
                      alt={p.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.04]"
                      onError={(e) => { if (p.image && e.currentTarget.src !== p.image) e.currentTarget.src = p.image; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <ShoppingBag className="h-8 w-8" />
                    </div>
                  )}
                  {p.is_cheapest && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold shadow">
                      <TrendingDown className="h-3 w-3" /> Cheapest
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 truncate">
                    {p.category || 'Shop'}
                  </div>
                  <div className="mt-0.5 text-sm font-semibold text-[#17352D] line-clamp-2 leading-snug min-h-[2.5rem]">
                    {p.name}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-base font-headline text-[#17352D]">
                      ${Number(p.price).toFixed(2)}
                    </div>
                    <span className="text-[11px] font-semibold text-[#C8922F] group-hover:underline">
                      Shop <ArrowRight className="inline h-3 w-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-6 md:hidden">
            <Link to="/shop" className="text-sm font-semibold text-[#17352D]">
              Shop all products →
            </Link>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">How It Works</div>
          <h2 className="mt-1 font-headline text-3xl md:text-4xl text-[#17352D]">From estimate to finished job in three simple steps</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: Camera, title: '1. Describe Your Project', text: 'Pick your service, upload a few photos, and tell us about your space.' },
            { icon: Sparkles, title: '2. Get an AI Ballpark', text: 'See a transparent labor + materials range in seconds, before any salesperson calls.' },
            { icon: Hammer, title: '3. Schedule the Pros', text: 'Approve, schedule, and watch your job progress through our customer portal.' },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-[#E6E9EE] bg-white p-6 shadow-[0_10px_30px_rgba(11,18,32,0.06)]">
              <div className="h-10 w-10 rounded-xl bg-[#F2F7F5] border border-[#C7DED4] flex items-center justify-center"><s.icon className="h-5 w-5 text-[#17352D]" /></div>
              <div className="mt-4 font-headline text-xl text-[#17352D]">{s.title}</div>
              <div className="mt-2 text-sm text-slate-600">{s.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Recent Work</div>
            <h2 className="font-headline text-3xl md:text-4xl text-[#17352D] mt-1">A few projects from our portfolio</h2>
          </div>
          <Link to="/gallery" data-testid="home-view-gallery-link" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#17352D]">View full gallery <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {gallery.slice(0, 6).map((g, i) => (
            <div key={g.id} className={`relative rounded-2xl overflow-hidden border border-[#E6E9EE] ${i === 0 ? 'md:col-span-2 md:row-span-2 aspect-square md:aspect-[1.4/1]' : 'aspect-[4/3]'}`}>
              <img src={fileUrl(g.image)} alt={g.title} loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/70 to-transparent text-white">
                <div className="text-[10px] uppercase tracking-[0.16em] opacity-80">{g.category}</div>
                <div className="text-sm md:text-base font-semibold">{g.title}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Reviews</div>
          <h2 className="mt-1 font-headline text-3xl md:text-4xl text-[#17352D]">Loved by South Florida homeowners</h2>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-2xl border border-[#E6E9EE] bg-white p-5 shadow-[0_10px_30px_rgba(11,18,32,0.06)]">
              <div className="flex items-center gap-1 text-[#C8922F]">{Array.from({length:5}).map((_,i)=><Star key={i} className="h-4 w-4 fill-current"/>)}</div>
              <p className="mt-3 text-slate-700">{r.text}</p>
              <div className="mt-4 text-sm text-slate-500">— {r.author}, {r.city}</div>
              {r.service && <div className="text-xs text-slate-400 mt-1">{r.service}</div>}
            </div>
          ))}
        </div>
      </section>

      <CtaBand />
    </Layout>
  );
}
